import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Bell,
  Calendar,
  User
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const UpdateView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [update, setUpdate] = useState(null)
  const [loading, setLoading] = useState(true)
  const { getUpdates } = useFirebase()

  useEffect(() => {
    loadUpdate()
  }, [id])

  const loadUpdate = async () => {
    try {
      setLoading(true)
      const updates = await getUpdates()
      const foundUpdate = updates.find(u => u.id === id)
      if (foundUpdate) {
        setUpdate(foundUpdate)
      } else {
        toast.error('Alert not found')
        navigate('/updates')
      }
    } catch (error) {
      console.error('Error loading update:', error)
      toast.error('Failed to load alert')
      navigate('/updates')
    } finally {
      setLoading(false)
    }
  }



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!update) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Alert not found</h3>
        <button onClick={() => navigate('/updates')} className="btn-primary">
          Back to Alerts
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/updates')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">View Alert</h1>
        </div>

      </div>

      {/* Update Details */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Alert Details</h3>
        </div>
        <div className="card-body space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-900 font-medium">{update.title}</p>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-900 whitespace-pre-wrap">{update.message}</p>
            </div>
          </div>

          {/* Link */}
          {update.link && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link
              </label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <a 
                  href={update.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline break-all"
                >
                  {update.link}
                </a>
              </div>
            </div>
          )}

          {/* Image */}
          {update.imageUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image
              </label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <img 
                  src={update.imageUrl} 
                  alt={update.title}
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            </div>
          )}

          

          {/* Created Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Created Date
            </label>
            <div className="p-3 bg-gray-50 rounded-lg flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <p className="text-gray-900">
                {update.createdAt 
                  ? new Date(update.createdAt).toLocaleDateString()
                  : update.createdDate || 'N/A'
                }
              </p>
            </div>
          </div>

          {/* Sent Date */}
          {update.sentAt && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sent Date
              </label>
              <div className="p-3 bg-gray-50 rounded-lg flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <p className="text-gray-900">
                  {new Date(update.sentAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {/* Created By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Created By
            </label>
            <div className="p-3 bg-gray-50 rounded-lg flex items-center space-x-2">
              <User className="h-4 w-4 text-gray-500" />
              <p className="text-gray-900">{update.createdBy || 'Admin'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdateView
